



























































































































































































































































































































































































































// CopyToClipboardButton is used only by the commented-out quick action icons and
// AccessLevelDynamicBadge only by the commented-out "Visible to" badge
import { DropdownMenu, SvgImage } from '@/app/components';
import { useFeatureFlags, useFilters } from '@/app/composable';
import store from '@/app/store';
import {
    // BellIcon, // used only by the commented-out quick action icons
    ClockIcon,
    // DocumentAddIcon, // used only by the commented-out quick action icons
    // DuplicateIcon, // used only by the commented-out quick action icons
    // LinkIcon, // used only by the commented-out quick action icons
    // MinusIcon, // used only by the commented-out quick action icons
    // PencilAltIcon, // used only by the commented-out quick action icons
    // RefreshIcon, // used only by the commented-out quick action icons
    // TrashIcon, // used only by the commented-out quick action icons
} from '@vue-hero-icons/outline';
import {
    AdjustmentsIcon,
    BanIcon,
    CalendarIcon,
    CheckIcon,
    DotsHorizontalIcon,
    DotsVerticalIcon,
    ExclamationIcon,
    LockClosedIcon,
    UserIcon,
    XIcon,
} from '@vue-hero-icons/solid';
import { PropType, computed, defineComponent } from '@vue/composition-api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import * as R from 'ramda';
import { TaskExecutionStatus, TaskType, WorkflowExecutionStatus, taskTypeLabels } from '../constants';
import { IndexedApolloPipeline, IndexedApolloPipelineTask, IndexedHarvesterSourceType } from '../types';

dayjs.extend(utc);

export default defineComponent({
    name: 'DataCheckinPipelineOverview',
    components: {
        UserIcon,
        CalendarIcon,
        // TrashIcon, // used only by the commented-out quick action icons
        // PencilAltIcon, // used only by the commented-out quick action icons
        // DocumentAddIcon, // used only by the commented-out quick action icons
        // DuplicateIcon, // used only by the commented-out quick action icons
        // LinkIcon, // used only by the commented-out quick action icons
        ClockIcon,
        // RefreshIcon, // used only by the commented-out quick action icons
        AdjustmentsIcon,
        CheckIcon,
        DotsHorizontalIcon,
        DotsVerticalIcon,
        XIcon,
        ExclamationIcon,
        BanIcon,
        LockClosedIcon,
        SvgImage,
        // AccessLevelDynamicBadge, // used only by the commented-out "Visible to" / "Created by" block
        // CopyToClipboardButton, // used only by the commented-out quick action icons
        DropdownMenu,
        // BellIcon, // used only by the commented-out quick action icons
        // MinusIcon, // used only by the commented-out quick action icons
    },
    props: {
        pipeline: {
            type: Object as PropType<IndexedApolloPipeline>,
            required: true,
        },
        alert: {
            type: Object as PropType<{ entityId: string | number; alertId: string; enabled: boolean }>,
            required: false,
        },
    },
    setup(props, { emit, root }: { emit: any; root: any }) {
        const { formatDate, formatDateTime } = useFilters();

        const { flag } = useFeatureFlags();
        const isAlertsEnabled = flag('alerts');

        const canRetry = computed(() => props.pipeline.executionStatus === WorkflowExecutionStatus.Cancelled);

        const orderedTasks = computed(() => R.sort(R.ascend<any>(R.prop('order')), props.pipeline.tasks));

        const statusTask: any = computed(() => {
            for (let idx = 0; idx < orderedTasks.value.length; idx += 1)
                if ([TaskExecutionStatus.Draft, TaskExecutionStatus.Updating].includes(orderedTasks.value[idx].status))
                    return orderedTasks.value[idx];

            for (let idx = 0; idx < orderedTasks.value.length; idx += 1)
                if (orderedTasks.value[idx].status !== TaskExecutionStatus.Completed) {
                    return orderedTasks.value[idx];
                }

            return orderedTasks.value[orderedTasks.value.length - 1];
        });

        const taskLabel = (type: TaskType): string => taskTypeLabels[type] ?? type;

        const statusMessage = computed(() => {
            if (canRetry.value) return WorkflowExecutionStatus.Cancelled;
            if (props.pipeline.executionStatus === WorkflowExecutionStatus.Ready) return 'execution pending';
            if (props.pipeline.executionStatus === WorkflowExecutionStatus.Updating) return 'update';
            if (props.pipeline.executionStatus === WorkflowExecutionStatus.Draft) return 'configuration';
            return props.pipeline.executionStatus;
        });

        const statusClass = computed(() => {
            switch (props.pipeline.executionStatus) {
                case WorkflowExecutionStatus.Updating:
                    return 'text-white bg-purple-500';
                case WorkflowExecutionStatus.Failed:
                    return 'text-white bg-red-600';
                case WorkflowExecutionStatus.Skipped:
                    return 'text-white bg-neutral-600';
                case WorkflowExecutionStatus.Deprecated:
                    return 'text-white bg-cherry-900';
                case WorkflowExecutionStatus.Cancelled:
                    return 'text-white bg-orange-800';
                case WorkflowExecutionStatus.Suspended:
                    return 'text-white bg-red-500';
                case WorkflowExecutionStatus.Completed:
                    return 'text-white bg-green-800';
                case WorkflowExecutionStatus.Queued:
                    return 'text-white bg-slate-500';
                case WorkflowExecutionStatus.Running:
                    return 'text-white bg-slate-700';
                case WorkflowExecutionStatus.Ready:
                    return 'text-white bg-teal-600';
                case WorkflowExecutionStatus.Draft:
                    return 'text-white bg-slateblue-700';
                default:
                    return 'text-white bg-slateblue-700';
            }
        });

        const harvesterSource = computed((): { label: string; colour: string; fileType?: string } | null => {
            let harvesterOption: { label: string; colour: string; fileType?: string } | null = null;
            if (props.pipeline?.harvesterSource) {
                switch (props.pipeline.harvesterSource) {
                    case IndexedHarvesterSourceType.Api:
                        harvesterOption = { label: 'External API', colour: 'bg-orange-300' };
                        break;
                    case IndexedHarvesterSourceType.InternalApi:
                        harvesterOption = { label: 'Platform API', colour: 'bg-green-300' };
                        break;
                    case IndexedHarvesterSourceType.File:
                        harvesterOption = {
                            label: 'File',
                            colour: 'bg-blue-300',
                        };
                        break;
                    case IndexedHarvesterSourceType.LargeFiles:
                        harvesterOption = {
                            label: 'Large Files',
                            colour: 'bg-slate-200',
                        };
                        break;
                    case IndexedHarvesterSourceType.Kafka:
                        harvesterOption = {
                            label: 'Kafka',
                            colour: 'bg-cherry-300',
                        };
                        break;
                    case IndexedHarvesterSourceType.ExternalKafka:
                        harvesterOption = {
                            label: 'External Kafka',
                            colour: 'bg-cherry-300',
                        };
                        break;
                    case IndexedHarvesterSourceType.MQTT:
                        harvesterOption = {
                            label: 'MQTT',
                            colour: 'bg-olive-300',
                        };
                        break;
                    case IndexedHarvesterSourceType.ExternalMQTT:
                        harvesterOption = {
                            label: 'External MQTT',
                            colour: 'bg-olive-300',
                        };
                        break;
                    case IndexedHarvesterSourceType.BigQuery:
                        harvesterOption = {
                            label: 'BigQuery',
                            colour: 'bg-purple-200',
                        };
                        break;
                    case IndexedHarvesterSourceType.SQL:
                        harvesterOption = {
                            label: 'SQL',
                            colour: 'bg-teal-300',
                        };
                        break;
                    case IndexedHarvesterSourceType.SAPHana:
                        harvesterOption = {
                            label: 'SAP Hana',
                            colour: 'bg-slateblue-300',
                        };
                        break;
                    default:
                        return { label: 'No ingestion source', colour: 'bg-red-300' };
                }

                if (
                    props.pipeline.harvesterSource !== IndexedHarvesterSourceType.InternalApi &&
                    props.pipeline.fileType
                ) {
                    harvesterOption['fileType'] = ['parquet', 'other'].includes(props.pipeline.fileType)
                        ? props.pipeline.fileType
                        : props.pipeline.fileType.toUpperCase();
                }
            }
            return harvesterOption;
        });

        const user = computed(() => store.state.auth.user);
        const isUserPipelineCreator = computed(() => user.value.id === Number(props.pipeline?.createdBy?.id));

        const editPipeline = async (pipeline: any) => {
            if (props.pipeline.executionStatus === WorkflowExecutionStatus.Suspended) {
                (root as any).$toastr.w(
                    `This ingestion job is suspended as its output asset (dataset) was deleted. You need to edit the Storage step and configure a new output asset in order to be able to use this pipeline again.`,
                    'Warning',
                );
            } else {
                emit('edit', pipeline);
            }
        };

        const appendData = async (pipeline: any) => {
            root.$router.push({ name: 'harvester', params: { id: pipeline.id } });
        };

        const displayAnyWarnings = (task: IndexedApolloPipelineTask): void => {
            if (task.type === 'harvester' && props.pipeline.executionStatus === WorkflowExecutionStatus.Suspended)
                (root as any).$toastr.w(
                    `This ingestion job is suspended as its output asset (dataset) was deleted. You need to edit the Storage step and configure a new output asset in order to be able to use this pipeline again`,
                    'Warning',
                );
        };

        const toTasks = (name: string, pipeline: any) => {
            return { name, params: { id: pipeline.id } };
        };

        const destroyPipeline = async (pipeline: any) => {
            if (!pipeline.canUse) {
                (root as any).$toastr.e('The Ingestion Job is locked', 'Error');
            } else {
                emit('destroy', pipeline);
            }
        };

        const goToAsset = (assetId: number) => {
            root.$router.push({ name: 'assets:view', params: { id: assetId } });
        };

        const isFinalized = computed(() => {
            if (props.pipeline.executionStatus === WorkflowExecutionStatus.Suspended) return true;
            return props.pipeline.tasks.every(
                (task: any) =>
                    !!task && ![WorkflowExecutionStatus.Draft, WorkflowExecutionStatus.Updating].includes(task.status),
            );
        });

        const accessLevel = computed(() => props.pipeline && props.pipeline?.accessLevel);

        const editAlert = () => {
            if (props.alert)
                root.$router.push({
                    name: 'alerts:edit',
                    params: { id: props.alert.alertId, backTo: 'data-checkin-jobs' },
                });
        };

        const clonePipeline = (pipeline: any) => {
            if (!isFinalized.value) {
                (root as any).$toastr.w('You cannot clone a non-finalised ingestion job', 'Warning');
            } else {
                emit('clone', pipeline);
            }
        };

        const copyPipelineId = () => {
            const value = `${props.pipeline.id}`;
            if (navigator.clipboard) navigator.clipboard.writeText(value);
            else {
                const input = document.createElement('input');
                input.value = value;
                document.body.appendChild(input);
                input.select();
                document.execCommand('copy');
                document.body.removeChild(input);
            }
            (root as any).$toastr.s('Ingestion job ID copied to clipboard', 'Success');
        };

        const taskPillClass = (status: TaskExecutionStatus): string => {
            switch (status) {
                case TaskExecutionStatus.Running:
                    return 'bg-primary-100 border-primary-200 text-primary-700';
                case TaskExecutionStatus.Ready:
                    return 'bg-orange-100 border-orange-200 text-orange-800';
                case TaskExecutionStatus.Queued:
                    return 'bg-orange-100 border-orange-200 text-orange-700';
                case TaskExecutionStatus.Failed:
                    return 'bg-red-100 border-red-200 text-red-700';
                case TaskExecutionStatus.Completed:
                    return 'bg-green-100 border-green-200 text-green-800';
                default:
                    return 'bg-neutral-100 border-neutral-200 text-neutral-600';
            }
        };

        const actionOptions = computed(() => {
            const options: { name: string; action: () => void }[] = [];
            if (canRetry.value) options.push({ name: 'Retry Execution', action: () => emit('resubmit') });
            if (isUserPipelineCreator.value) options.push({ name: 'Edit', action: () => editPipeline(props.pipeline) });
            if (
                props.pipeline.harvesterSource === IndexedHarvesterSourceType.File &&
                props.pipeline.hasExecuted &&
                props.pipeline.executionStatus !== WorkflowExecutionStatus.Suspended
            )
                options.push({ name: 'Append Data', action: () => appendData(props.pipeline) });
            // 'Navigate to Asset' entries removed from the actions menu.
            // Kept commented out in case they need to be restored.
            // (props.pipeline.provenanceAssetIds || []).forEach((assetId: number, idx: number) => {
            //     options.push({
            //         name: `Navigate to Asset${props.pipeline.provenanceAssetIds.length > 1 ? ` ${idx + 1}` : ''}`,
            //         action: () => goToAsset(assetId),
            //     });
            // });
            options.push({ name: 'Clone', action: () => clonePipeline(props.pipeline) });
            options.push({ name: 'Execution History', action: () => emit('history', props.pipeline.id) });
            if (isAlertsEnabled.value && props.alert)
                options.push({ name: 'Navigate to Alert', action: () => editAlert() });
            // 'Copy ID' removed from the actions menu. Kept commented out in case it needs to be restored.
            // options.push({ name: 'Copy ID', action: () => copyPipelineId() });
            if (isUserPipelineCreator.value)
                options.push({ name: 'Delete', action: () => destroyPipeline(props.pipeline) });
            return options;
        });

        return {
            IndexedHarvesterSourceType,
            dayjs,
            formatDate,
            formatDateTime,
            statusMessage,
            statusClass,
            WorkflowExecutionStatus,
            TaskExecutionStatus,
            orderedTasks,
            statusTask,
            canRetry,
            isUserPipelineCreator,
            editPipeline,
            appendData,
            toTasks,
            destroyPipeline,
            isFinalized,
            accessLevel,
            harvesterSource,
            isAlertsEnabled,
            goToAsset,
            displayAnyWarnings,
            editAlert,
            clonePipeline,
            copyPipelineId,
            actionOptions,
            taskPillClass,
            taskLabel,
        };
    },
});
